import { InputAdornment, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useShop } from '../../../shop/ShopProvider';
import { currencySymbol } from '../lib/api';
import type { Discount, DiscountType } from '../lib/totals';

/** Discount as an amount or a percentage, with a compact unit toggle inside the field. */
export function DiscountInput({
  value,
  onChange,
  label = 'Discount',
  disabled,
  error,
  helperText,
  size,
  autoFocus,
  inputRef,
}: {
  value: Discount | null;
  onChange: (value: Discount | null) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const { format } = useShop();
  const type: DiscountType = value?.type ?? 'PERCENT';
  return (
    <TextField
      label={label}
      value={value?.value ?? ''}
      disabled={disabled}
      error={error}
      helperText={helperText}
      size={size}
      autoFocus={autoFocus}
      inputRef={inputRef}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d.]/g, '');
        onChange(v === '' ? null : { type, value: v });
      }}
      slotProps={{
        htmlInput: { inputMode: 'decimal', className: 'tabular', 'aria-label': label },
        input: {
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: -0.75 }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={type}
                disabled={disabled}
                onChange={(_, t: DiscountType | null) => {
                  if (t) onChange({ type: t, value: value?.value ?? '' });
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1,
                    py: 0.25,
                    fontSize: '0.75rem',
                    fontWeight: 650,
                    lineHeight: 1.6,
                    border: 0,
                    borderRadius: '7px !important',
                  },
                  bgcolor: 'action.hover',
                  borderRadius: '9px',
                  p: 0.25,
                }}
              >
                <ToggleButton value="PERCENT" aria-label="Percent">
                  %
                </ToggleButton>
                <ToggleButton value="AMOUNT" aria-label="Amount">
                  {currencySymbol(format)}
                </ToggleButton>
              </ToggleButtonGroup>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
